/**
 * Public AEO Benchmark Page
 *
 * Lead capture tool that analyzes web content for LLM-readiness.
 * Users can see a sample result without registration, but must
 * register to analyze their own pages.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import {
  BarChart3,
  Brain,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Zap,
  Target,
  FileText,
  Quote,
  Search,
  ArrowRight,
  Loader2,
  Mail,
  Building,
  Globe,
  Sparkles,
  TrendingUp,
  Shield,
  Bot
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface BenchmarkMetrics {
  semantic_density?: {
    sdi_score: number;
    noise_ratio: number;
    technical_tokens: number;
    marketing_tokens: number;
    meets_sdi_target: boolean;
  };
  answerability?: {
    answer_coverage: number;
    specificity_score: number;
    grounding_rate: number;
    complete_answers: number;
    partial_answers: number;
    missing_answers: number;
    meets_coverage_target: boolean;
  };
  citation_readiness?: {
    citation_readiness_score: number;
    evidence_span_count: number;
    tables_count: number;
    faqs_count: number;
    meets_target: boolean;
  };
  spec_completeness?: {
    spec_completeness: number;
    found_specs: string[];
    missing_specs: string[];
    meets_completeness_target: boolean;
  };
  eav_extraction?: {
    triplet_count: number;
    triplet_density: number;
    typed_value_ratio: number;
  };
  structural_quality?: {
    schema_coverage: number;
    has_product_schema: boolean;
    has_faq_schema: boolean;
  };
  // New metrics
  hallucination_pressure?: {
    pressure_score: number;
    grounded_responses: number;
    inferred_responses: number;
    meets_target: boolean;
  };
  retrieval_quality?: {
    retrieval_score: number;
    chunk_survival_rate: number;
    meets_target: boolean;
  };
}

interface BenchmarkResult {
  success: boolean;
  baseline?: {
    metrics: BenchmarkMetrics;
    composite_score: number;
  };
  analysis?: {
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
  };
  scorecard?: {
    dimensions: Array<{
      name: string;
      baseline: string;
      target: string;
    }>;
  };
  cta?: {
    message: string;
    action: string;
  };
  duration_ms?: number;
  costs?: {
    total: number;
  };
  // Multi-model results
  multi_model?: {
    models: string[];
    stability_score: number;
    results_by_model: Record<string, any>;
  };
}

export default function AEOBenchmark() {
  const [activeTab, setActiveTab] = useState('intro');
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('benchmark_token')
  );

  // Registration form
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState('');

  // Analysis
  const [urlToAnalyze, setUrlToAnalyze] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<BenchmarkResult | null>(null);
  const [analyzeError, setAnalyzeError] = useState('');

  // Sample result for demo
  const [sampleResult, setSampleResult] = useState<BenchmarkResult | null>(null);

  useEffect(() => {
    // Load sample result on mount
    loadSampleResult();
  }, []);

  const loadSampleResult = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/benchmark/sample`);
      const data = await response.json();
      setSampleResult(data);
    } catch (error) {
      console.error('Failed to load sample:', error);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistering(true);
    setRegisterError('');

    try {
      const response = await fetch(`${API_URL}/api/public/benchmark/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company, website })
      });

      const data = await response.json();

      if (data.success) {
        setAccessToken(data.access_token);
        localStorage.setItem('benchmark_token', data.access_token);
        setActiveTab('analyze');
      } else {
        setRegisterError(data.error || 'Registration failed');
      }
    } catch (error) {
      setRegisterError('Network error. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    setAnalyzing(true);
    setAnalyzeError('');
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/public/benchmark/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Benchmark-Token': accessToken || ''
        },
        body: JSON.stringify({ url: urlToAnalyze })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setActiveTab('results');
      } else {
        setAnalyzeError(data.error || 'Analysis failed');
        if (response.status === 401) {
          // Token expired
          localStorage.removeItem('benchmark_token');
          setAccessToken(null);
          setActiveTab('register');
        }
      }
    } catch (error) {
      setAnalyzeError('Network error. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 70) return <Badge className="bg-green-100 text-green-800">AI Ready</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-100 text-yellow-800">Needs Work</Badge>;
    return <Badge className="bg-red-100 text-red-800">Not Optimized</Badge>;
  };

  // Prepare radar chart data
  const prepareRadarData = (metrics: BenchmarkMetrics) => {
    return [
      {
        metric: 'Semantic Density',
        value: (metrics.semantic_density?.sdi_score || 0) * 100,
        fullMark: 100
      },
      {
        metric: 'Answerability',
        value: (metrics.answerability?.answer_coverage || 0) * 100,
        fullMark: 100
      },
      {
        metric: 'Citation Ready',
        value: (metrics.citation_readiness?.citation_readiness_score || 0) * 100,
        fullMark: 100
      },
      {
        metric: 'Spec Complete',
        value: (metrics.spec_completeness?.spec_completeness || 0) * 100,
        fullMark: 100
      },
      {
        metric: 'Structure',
        value: (metrics.structural_quality?.schema_coverage || 0) * 100,
        fullMark: 100
      },
      {
        metric: 'Low Hallucination',
        value: metrics.hallucination_pressure
          ? (1 - metrics.hallucination_pressure.pressure_score) * 100
          : 50,
        fullMark: 100
      }
    ];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Bot className="h-8 w-8" />
              <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
                Free Tool
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              AEO Benchmark Engine
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Measure how well your content performs for AI-powered discovery.
              See if ChatGPT, Perplexity, and other AI assistants can find and cite your pages.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={() => setActiveTab(accessToken ? 'analyze' : 'register')}
              >
                <Zap className="h-5 w-5 mr-2" />
                Analyze Your Page
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-white text-white hover:bg-white/10"
                onClick={() => setActiveTab('intro')}
              >
                See How It Works
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-5xl mx-auto">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="intro">
              <FileText className="h-4 w-4 mr-2" />
              How It Works
            </TabsTrigger>
            <TabsTrigger value="register" disabled={!!accessToken}>
              <Mail className="h-4 w-4 mr-2" />
              {accessToken ? 'Registered' : 'Register'}
            </TabsTrigger>
            <TabsTrigger value="analyze" disabled={!accessToken}>
              <Search className="h-4 w-4 mr-2" />
              Analyze
            </TabsTrigger>
            <TabsTrigger value="results" disabled={!result}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Results
            </TabsTrigger>
          </TabsList>

          {/* Intro Tab */}
          <TabsContent value="intro" className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-600" />
                    Why AEO Matters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    AI assistants like ChatGPT, Perplexity, and Google's AI Overview are
                    changing how people discover products and information. Content optimized
                    for humans isn't optimized for AI.
                  </p>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-amber-800 font-medium">
                      75% of pages we've analyzed fail basic AEO requirements.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    What We Measure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <span><strong>Semantic Density</strong> - Technical content vs marketing fluff</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <span><strong>Answerability</strong> - Can AI answer user questions from your content?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <span><strong>Citation Readiness</strong> - Is your content quotable?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      <span><strong>Hallucination Risk</strong> - Does your content prevent AI mistakes?</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Sample Result Preview */}
            {sampleResult?.baseline && (
              <Card className="border-2 border-dashed">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    Sample Result (Before AEO Optimization)
                  </CardTitle>
                  <CardDescription>
                    This is what a typical unoptimized page scores. Register to see your actual results.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className={`text-5xl font-bold ${getScoreColor(sampleResult.baseline.composite_score)}`}>
                        {sampleResult.baseline.composite_score}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Composite Score</p>
                      {getScoreBadge(sampleResult.baseline.composite_score)}
                    </div>
                    <div className="md:col-span-2">
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={prepareRadarData(sampleResult.baseline.metrics)}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                            <Radar
                              name="Score"
                              dataKey="value"
                              stroke="#ef4444"
                              fill="#ef4444"
                              fillOpacity={0.3}
                            />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button onClick={() => setActiveTab(accessToken ? 'analyze' : 'register')}>
                      Analyze Your Page
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <Card className="max-w-md mx-auto">
              <CardHeader>
                <CardTitle>Register for Free Access</CardTitle>
                <CardDescription>
                  Get 5 free page analyses per day. No credit card required.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="company"
                        type="text"
                        placeholder="Your company name"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="website"
                        type="url"
                        placeholder="https://yoursite.com"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {registerError && (
                    <Alert variant="destructive">
                      <AlertDescription>{registerError}</AlertDescription>
                    </Alert>
                  )}

                  <Button type="submit" className="w-full" disabled={registering}>
                    {registering ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        Get Free Access
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    By registering, you agree to receive occasional updates about AEO best practices.
                  </p>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analyze Tab */}
          <TabsContent value="analyze">
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Analyze Your Page</CardTitle>
                <CardDescription>
                  Enter a URL to measure its AI-readiness. Analysis takes about 30-60 seconds.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAnalyze} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="url">Page URL</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="url"
                          type="url"
                          placeholder="https://example.com/product-page"
                          value={urlToAnalyze}
                          onChange={(e) => setUrlToAnalyze(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                      <Button type="submit" disabled={analyzing}>
                        {analyzing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Search className="h-4 w-4 mr-2" />
                            Analyze
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {analyzeError && (
                    <Alert variant="destructive">
                      <AlertDescription>{analyzeError}</AlertDescription>
                    </Alert>
                  )}

                  {analyzing && (
                    <div className="space-y-4 py-8">
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
                        <p className="mt-4 text-gray-600">Analyzing your page...</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Fetching content...
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Running AI analysis...
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <div className="h-4 w-4" />
                          Calculating scores...
                        </div>
                      </div>
                    </div>
                  )}
                </form>

                <div className="mt-8 pt-8 border-t">
                  <h4 className="font-medium mb-4">What we analyze:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-purple-600" />
                      Semantic structure
                    </div>
                    <div className="flex items-center gap-2">
                      <Quote className="h-4 w-4 text-blue-600" />
                      Citation-ready content
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-600" />
                      Question coverage
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-amber-600" />
                      Hallucination prevention
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            {result?.baseline && (
              <div className="space-y-8">
                {/* Score Overview */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>AEO Score</CardTitle>
                      {result.duration_ms && (
                        <Badge variant="outline">
                          Analysis completed in {(result.duration_ms / 1000).toFixed(1)}s
                        </Badge>
                      )}
                    </div>
                    <CardDescription>
                      {urlToAnalyze}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-8">
                      <div className="text-center">
                        <div className={`text-6xl font-bold ${getScoreColor(result.baseline.composite_score)}`}>
                          {result.baseline.composite_score}
                        </div>
                        <p className="text-gray-500 mt-2">out of 100</p>
                        <div className="mt-2">
                          {getScoreBadge(result.baseline.composite_score)}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={prepareRadarData(result.baseline.metrics)}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                              <PolarRadiusAxis angle={30} domain={[0, 100]} />
                              <Radar
                                name="Your Score"
                                dataKey="value"
                                stroke="#3b82f6"
                                fill="#3b82f6"
                                fillOpacity={0.4}
                              />
                              <Tooltip />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Metrics */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Semantic Density */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-600" />
                        Semantic Density
                        {result.baseline.metrics.semantic_density?.meets_sdi_target ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Technical Content</span>
                            <span>{((result.baseline.metrics.semantic_density?.sdi_score || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <Progress
                            value={(result.baseline.metrics.semantic_density?.sdi_score || 0) * 100}
                            className="h-2"
                          />
                        </div>
                        <p className="text-sm text-gray-500">
                          {result.baseline.metrics.semantic_density?.technical_tokens || 0} technical tokens vs{' '}
                          {result.baseline.metrics.semantic_density?.marketing_tokens || 0} marketing tokens
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Answerability */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="h-5 w-5 text-blue-600" />
                        Answerability
                        {result.baseline.metrics.answerability?.meets_coverage_target ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Questions Answerable</span>
                            <span>{((result.baseline.metrics.answerability?.answer_coverage || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <Progress
                            value={(result.baseline.metrics.answerability?.answer_coverage || 0) * 100}
                            className="h-2"
                          />
                        </div>
                        <p className="text-sm text-gray-500">
                          {result.baseline.metrics.answerability?.complete_answers || 0} complete,{' '}
                          {result.baseline.metrics.answerability?.partial_answers || 0} partial,{' '}
                          {result.baseline.metrics.answerability?.missing_answers || 0} missing
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Citation Readiness */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Quote className="h-5 w-5 text-green-600" />
                        Citation Readiness
                        {result.baseline.metrics.citation_readiness?.meets_target ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Citation Score</span>
                            <span>{((result.baseline.metrics.citation_readiness?.citation_readiness_score || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <Progress
                            value={(result.baseline.metrics.citation_readiness?.citation_readiness_score || 0) * 100}
                            className="h-2"
                          />
                        </div>
                        <p className="text-sm text-gray-500">
                          {result.baseline.metrics.citation_readiness?.evidence_span_count || 0} evidence spans,{' '}
                          {result.baseline.metrics.citation_readiness?.tables_count || 0} tables,{' '}
                          {result.baseline.metrics.citation_readiness?.faqs_count || 0} FAQs
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Spec Completeness */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="h-5 w-5 text-amber-600" />
                        Spec Completeness
                        {result.baseline.metrics.spec_completeness?.meets_completeness_target ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Specs Found</span>
                            <span>{((result.baseline.metrics.spec_completeness?.spec_completeness || 0) * 100).toFixed(0)}%</span>
                          </div>
                          <Progress
                            value={(result.baseline.metrics.spec_completeness?.spec_completeness || 0) * 100}
                            className="h-2"
                          />
                        </div>
                        {result.baseline.metrics.spec_completeness?.missing_specs &&
                          result.baseline.metrics.spec_completeness.missing_specs.length > 0 && (
                          <p className="text-sm text-red-600">
                            Missing: {result.baseline.metrics.spec_completeness.missing_specs.slice(0, 3).join(', ')}
                            {result.baseline.metrics.spec_completeness.missing_specs.length > 3 && '...'}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* RAG Retrieval Quality */}
                  {result.baseline.metrics.retrieval_quality && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Search className="h-5 w-5 text-indigo-600" />
                          RAG Retrieval Quality
                          {result.baseline.metrics.retrieval_quality?.meets_retrieval_target ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Retrieval Score</span>
                              <span>{((result.baseline.metrics.retrieval_quality?.retrieval_score || 0) * 100).toFixed(0)}%</span>
                            </div>
                            <Progress
                              value={(result.baseline.metrics.retrieval_quality?.retrieval_score || 0) * 100}
                              className="h-2"
                            />
                          </div>
                          <p className="text-sm text-gray-500">
                            {result.baseline.metrics.retrieval_quality?.substantial_chunks || 0} of{' '}
                            {result.baseline.metrics.retrieval_quality?.total_chunks || 0} chunks contain substantial info
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Hallucination Prevention */}
                  {result.baseline.metrics.hallucination_pressure && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Shield className="h-5 w-5 text-rose-600" />
                          Hallucination Prevention
                          {result.baseline.metrics.hallucination_pressure?.meets_target ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Grounding Rate</span>
                              <span>{((result.baseline.metrics.hallucination_pressure?.grounding_rate || 0) * 100).toFixed(0)}%</span>
                            </div>
                            <Progress
                              value={(result.baseline.metrics.hallucination_pressure?.grounding_rate || 0) * 100}
                              className="h-2"
                            />
                          </div>
                          <p className="text-sm text-gray-500">
                            {result.baseline.metrics.hallucination_pressure?.grounded_responses || 0} grounded,{' '}
                            {result.baseline.metrics.hallucination_pressure?.inferred_responses || 0} inferred,{' '}
                            {result.baseline.metrics.hallucination_pressure?.refused_responses || 0} refused
                          </p>
                          {result.baseline.metrics.hallucination_pressure?.grounding_signals !== undefined && (
                            <p className="text-xs text-gray-400">
                              {result.baseline.metrics.hallucination_pressure.grounding_signals} grounding signals found in content
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Analysis & Recommendations */}
                {result.analysis && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Analysis & Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="font-medium text-red-600 mb-3 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Issues Found
                          </h4>
                          <ul className="space-y-2">
                            {result.analysis.weaknesses.map((weakness, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                                {weakness}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-green-600 mb-3 flex items-center gap-2">
                            <Zap className="h-4 w-4" />
                            Recommendations
                          </h4>
                          <ul className="space-y-2">
                            {result.analysis.recommendations.map((rec, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <ArrowRight className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* CTA */}
                {result.cta && (
                  <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <CardContent className="py-8">
                      <div className="text-center max-w-2xl mx-auto">
                        <h3 className="text-2xl font-bold mb-2">{result.cta.message}</h3>
                        <p className="text-blue-100 mb-6">{result.cta.action}</p>
                        <Button
                          size="lg"
                          variant="secondary"
                          onClick={() => window.open('https://www.pagemind.ai/demo?source=benchmark', '_blank')}
                        >
                          Request a Demo
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">
            AEO Benchmark Engine by{' '}
            <a href="https://www.pagemind.ai" className="text-white hover:underline">
              PageMind
            </a>
            {' '}- Optimize your content for AI-powered discovery.
          </p>
        </div>
      </footer>
    </div>
  );
}
